import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Button,
  Checkbox,
  Chip,
  Icon,
  Select,
  Toast,
  type ChipColor,
  type Column,
  type SelectOption,
  type ToastType,
} from "@legal-500/legal500-design-system";
import { DataTable } from "@/components/DataTable/DataTable";
import EmptyState from "@/components/EmptyState";
import LoadingState from "@/components/LoadingState";
import { PageHeader } from "@/components/PageHeader";
import { UnauthorizedView } from "@/components/UnauthorizedView";
import { usePermissions } from "@/hooks/usePermissions";
import {
  templates,
  type MailingTemplate,
  type TemplateCounts,
  type TemplateStatus,
  type TemplateType,
} from "@/api/templates.service";
import { formatDateShort } from "@/utils/dateFormatter";
import { EditTypeModal } from "./EditTypeModal";
import { PreviewModal } from "./PreviewModal";
import styles from "./MailingTemplates.module.css";

const SUBTITLE =
  "Templates are authored and stored in SendGrid. Assign a type to each template so it becomes available for campaigns.";

const TYPE_FILTER_OPTIONS: SelectOption[] = [
  { value: "", label: "All types" },
  { value: "survey_request", label: "Survey request" },
  { value: "reminder", label: "Reminder" },
];

// Read-only display labels for the Type column.
const TYPE_LABEL: Record<TemplateType, string> = {
  survey_request: "Survey request",
  reminder: "Reminder",
};

const STATUS_LABEL: Record<TemplateStatus, string> = {
  configured: "Configured",
  unconfigured: "Unconfigured",
  archived: "Archived",
};

const STATUS_COLOR: Record<TemplateStatus, ChipColor> = {
  configured: "success",
  unconfigured: "default",
  archived: "secondary",
};

function MailingTemplatesPage() {
  const { hasPermission } = usePermissions();
  const canWrite = hasPermission("staff.campaign.write");

  const [typeFilter, setTypeFilter] = useState("");
  const [onlyUnconfigured, setOnlyUnconfigured] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  const [rows, setRows] = useState<MailingTemplate[]>([]);
  const [counts, setCounts] = useState<TemplateCounts | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<(Error & { status?: number; code?: string }) | null>(null);

  const [previewTemplate, setPreviewTemplate] = useState<MailingTemplate | null>(null);
  const [editTemplate, setEditTemplate] = useState<MailingTemplate | null>(null);
  const [savingId, setSavingId] = useState<string | null>(null);
  const [toast, setToast] = useState<{ message: string; type: ToastType } | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    templates
      .list({
        type: (typeFilter as TemplateType) || undefined,
        onlyUnconfigured: onlyUnconfigured || undefined,
      })
      .then(resp => {
        if (cancelled) return;
        setRows(resp.templates);
        setCounts(resp.counts);
      })
      .catch(err => {
        if (!cancelled) {
          setError(err instanceof Error ? err : new Error(String(err)));
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [typeFilter, onlyUnconfigured, reloadKey]);

  const triggerReload = useCallback(() => setReloadKey(k => k + 1), []);

  // Apply a config change (type or archived). Optimistically patch the affected
  // row from the response, then trigger a refetch so server-side filters
  // (onlyUnconfigured) and counts stay consistent.
  const applyConfig = useCallback(
    async (
      template: MailingTemplate,
      change: { type?: TemplateType | null; archived?: boolean },
      successMessage: string,
    ) => {
      setSavingId(template.sendgrid_template_id);
      try {
        const resp = await templates.setConfig(template.sendgrid_template_id, change);
        setRows(prev =>
          prev.map(row =>
            row.sendgrid_template_id === template.sendgrid_template_id
              ? {
                  ...row,
                  type: resp.template.type,
                  archived: resp.template.archived,
                  status: resp.template.status,
                }
              : row,
          ),
        );
        setToast({ message: successMessage, type: "success" });
        triggerReload();
      } catch (err) {
        setToast({
          message: err instanceof Error ? err.message : "Failed to update template",
          type: "error",
        });
      } finally {
        setSavingId(null);
      }
    },
    [triggerReload],
  );

  const handleToggleArchive = useCallback(
    (template: MailingTemplate) => {
      void applyConfig(
        template,
        { archived: !template.archived },
        template.archived ? "Template unarchived." : "Template archived.",
      );
    },
    [applyConfig],
  );

  // Patch the affected row from the Edit modal's response, then refetch so
  // server-side filters (onlyUnconfigured) and counts stay consistent.
  const handleTypeSaved = useCallback(
    (
      templateId: string,
      next: { type: TemplateType | null; archived: boolean; status: TemplateStatus },
    ) => {
      setRows(prev =>
        prev.map(row => (row.sendgrid_template_id === templateId ? { ...row, ...next } : row)),
      );
      setEditTemplate(null);
      setToast({ message: "Template type updated.", type: "success" });
      triggerReload();
    },
    [triggerReload],
  );

  const columns: Column<MailingTemplate>[] = useMemo(() => {
    const base: Column<MailingTemplate>[] = [
      {
        header: "Template",
        accessor: "name",
        render: (_value: unknown, row: MailingTemplate) => (
          <div className={styles.templateCell}>
            <span className={styles.templateName}>{row.name ?? "—"}</span>
            <span className={styles.templateId}>{row.sendgrid_template_id}</span>
          </div>
        ),
      },
      {
        header: "Subject",
        accessor: "subject",
        render: (value: unknown) => (value ? String(value) : "—"),
      },
      {
        header: "Type",
        accessor: "type",
        render: (_value: unknown, row: MailingTemplate) => (
          <span className={styles.typeCell}>{row.type ? TYPE_LABEL[row.type] : "—"}</span>
        ),
      },
      {
        header: "Status",
        accessor: "status",
        render: (_value: unknown, row: MailingTemplate) => {
          if (row.missing_in_sendgrid) {
            return (
              <Chip size="small" variant="filled" color="error">
                Removed from SendGrid
              </Chip>
            );
          }
          return (
            <Chip
              size="small"
              variant="filled"
              color={STATUS_COLOR[row.status]}
              className={row.status === "unconfigured" ? styles.unconfiguredStatusChip : undefined}
            >
              {STATUS_LABEL[row.status]}
            </Chip>
          );
        },
      },
      {
        header: "Last modified",
        accessor: "updated_at",
        render: (value: unknown) => {
          if (!value) return "—";
          try {
            return formatDateShort(String(value));
          } catch {
            return "—";
          }
        },
      },
      {
        // A single trailing actions column: Preview (+ Edit / Archive when the
        // user has write access). Header is empty like other action columns.
        header: "",
        accessor: "sendgrid_template_id",
        render: (_value: unknown, row: MailingTemplate) => {
          const label = row.name ?? row.sendgrid_template_id;
          const busy = savingId === row.sendgrid_template_id;
          return (
            <div className={styles.actionsCell}>
              <Button
                variant="outlined"
                size="small"
                aria-label={`Preview ${label}`}
                disabled={row.missing_in_sendgrid || !row.subject}
                onClick={() => setPreviewTemplate(row)}
              >
                <Icon icon="magnifier" size="small" /> Preview
              </Button>
              {canWrite && (
                <>
                  <Button
                    variant="outlined"
                    size="small"
                    aria-label={`Edit type for ${label}`}
                    disabled={row.missing_in_sendgrid || busy}
                    onClick={() => setEditTemplate(row)}
                  >
                    <Icon icon="edit" size="small" />
                  </Button>
                  <Button
                    variant="outlined"
                    size="small"
                    disabled={row.missing_in_sendgrid || busy}
                    aria-label={`${row.archived ? "Unarchive" : "Archive"} ${label}`}
                    onClick={() => handleToggleArchive(row)}
                  >
                    <Icon icon={row.archived ? "check" : "delete"} size="small" />
                  </Button>
                </>
              )}
            </div>
          );
        },
      },
    ];

    return base;
  }, [canWrite, savingId, handleToggleArchive]);

  const isUpstreamError = error?.status === 502 || error?.code === "UPSTREAM_ERROR";

  if (error?.status === 401 || error?.status === 403) {
    return <UnauthorizedView />;
  }

  return (
    <div className={styles.wrapper}>
      <PageHeader title="Mailing Templates" subtitle={SUBTITLE} />

      <div className={styles.filterRow}>
        <div className={styles.filterField}>
          <Select
            label="Type"
            value={typeFilter}
            onChange={e => setTypeFilter(e.target.value)}
            options={TYPE_FILTER_OPTIONS}
            size="small"
          />
        </div>
        <div className={styles.checkboxField}>
          <Checkbox
            label="Show only unconfigured"
            checked={onlyUnconfigured}
            onChange={e => setOnlyUnconfigured(e.target.checked)}
          />
        </div>
      </div>

      {!loading && !error && counts && counts.missing_in_sendgrid > 0 && (
        <div className={styles.warningBanner} role="alert">
          <Icon icon="exclamation" size="small" />
          <span>
            {counts.missing_in_sendgrid} configured template
            {counts.missing_in_sendgrid === 1 ? "" : "s"} no longer exist in SendGrid.
          </span>
        </div>
      )}

      {loading && <LoadingState message="Loading templates…" />}

      {error && !loading && isUpstreamError && (
        <EmptyState
          variant="error"
          title="Couldn't load templates from SendGrid"
          message="Couldn't load templates from SendGrid — check the API key permissions."
        />
      )}

      {error && !loading && !isUpstreamError && (
        <EmptyState variant="error" title="Failed to load templates" message={error.message} />
      )}

      {!loading && !error && (
        <DataTable
          data={rows}
          columns={columns}
          size="small"
          rowKey="sendgrid_template_id"
          emptyMessage="No templates found."
        />
      )}

      {previewTemplate && (
        <PreviewModal template={previewTemplate} onClose={() => setPreviewTemplate(null)} />
      )}

      {editTemplate && (
        <EditTypeModal
          template={editTemplate}
          onClose={() => setEditTemplate(null)}
          onSaved={next => handleTypeSaved(editTemplate.sendgrid_template_id, next)}
        />
      )}

      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          duration={toast.type === "error" ? 5000 : 3000}
          onClose={() => setToast(null)}
        />
      )}
    </div>
  );
}

export default MailingTemplatesPage;
